import { useState, useContext, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import UserContext from "contexts/UserContext.js";
import { useCustomerSearch } from "hooks/useCustomerSearch.js";
import {
  downloadAssignmentTemplate,
  downloadAssignmentCatalogs,
  uploadAssignmentFile,
  previewAssignmentImport,
  applyAssignmentImport,
  getAssignmentImportJobStatus,
  listAssignmentImportJobs,
  previewAssignmentRollback,
  applyAssignmentRollback,
} from "services/assignmentImportService.js";
import {
  // CustomerInfoCard,
  JobHistoryCard,
  ImportUploadCard,
  ImportPreviewCard,
} from "./components/AssignmentImportCards.jsx";
import { WorkflowChooserCard } from "./components/WorkflowChooserCard.jsx";
import { TemplateDownloadModal } from "./components/TemplateDownloadModal.jsx";
import { CustomerSelector } from "./components/CustomerSelector.jsx";

const AssignmentImportTabPage = () => {
  const { user } = useContext(UserContext);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [templateOptions, setTemplateOptions] = useState({
    prepopulated: false,
    include_catalogs: true,
    format: "xlsx",
  });
  const [isDownloading, setIsDownloading] = useState(false);
  const [catalogOptions, setCatalogOptions] = useState({
    format: "xlsx",
    include_users: true,
  });
  const [isDownloadingCatalogs, setIsDownloadingCatalogs] = useState(false);

  // Separate error states for each card
  const [templateError, setTemplateError] = useState(null);
  const [catalogsError, setCatalogsError] = useState(null);
  const [importError, setImportError] = useState(null);

  // Import flow state
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [importJobId, setImportJobId] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [applyMessage, setApplyMessage] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [debugDelayMs, setDebugDelayMs] = useState(0);

  const [job_list, setJobList] = useState([]);
  const [job_list_loading, setJobListLoading] = useState(false);
  const [job_list_error, setJobListError] = useState(null);
  const [job_list_page, setJobListPage] = useState(0);
  const [job_list_per_page, setJobListPerPage] = useState(10);
  const [job_list_total, setJobListTotal] = useState(0);
  const [job_status_filter, setJobStatusFilter] = useState("all");
  const [job_reload_token, setJobReloadToken] = useState(0);
  const [import_section_active, setImportSectionActive] = useState(false);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [template_modal_open, setTemplateModalOpen] = useState(false);
  const [job_action_state, setJobActionState] = useState(null);

  const {
    customers,
    loading: searchLoading,
    searchCustomers,
    clearSearch,
  } = useCustomerSearch();

  const isSysAdmin = user?.role_names?.includes("sysadmin");
  const isCustomerAdmin = user?.role_names?.includes("kd_verwaltung");

  const effectiveCustomerId = isSysAdmin
    ? (selectedCustomer?.id_ov ?? null)
    : (user?.kunde_id_ov ?? null);

  useEffect(() => {
    if (isSysAdmin) {
      searchCustomers("");
    }
  }, [isSysAdmin, searchCustomers]);

  useEffect(() => {
    setJobListPage(0);
    setJobStatusFilter("all");
    setCurrentFlow(null);
    setImportSectionActive(false);
    setPreviewResult(null);
    setImportJobId(null);
    setSelectedFile(null);
    setApplyMessage(null);
    setJobStatus(null);
    setJobReloadToken((prev) => prev + 1);
  }, [effectiveCustomerId]);

  const fetch_job_list = useCallback(async () => {
    if (!effectiveCustomerId) {
      setJobList([]);
      setJobListTotal(0);
      setJobListError(null);
      return;
    }

    setJobListLoading(true);
    setJobListError(null);

    try {
      const response = await listAssignmentImportJobs(effectiveCustomerId, {
        page: job_list_page + 1,
        per_page: job_list_per_page,
        status: job_status_filter !== "all" ? job_status_filter : undefined,
      });

      setJobList(response.data ?? []);
      setJobListTotal(response.meta?.total ?? 0);
    } catch (err) {
      console.error("Job list fetch failed:", err);
      setJobListError(
        "Fehler beim Laden der Import-Jobs. Bitte versuchen Sie es erneut."
      );
    } finally {
      setJobListLoading(false);
    }
  }, [
    effectiveCustomerId,
    job_list_page,
    job_list_per_page,
    job_status_filter,
  ]);

  useEffect(() => {
    fetch_job_list();
  }, [fetch_job_list, job_reload_token]);

  const is_job_action_running = (job_id, action) =>
    job_action_state &&
    job_action_state.job_id === job_id &&
    job_action_state.action === action;

  const handle_refresh_jobs = () => {
    setJobReloadToken((prev) => prev + 1);
  };

  const handle_status_filter_change = (event) => {
    setJobStatusFilter(event.target.value);
    setJobListPage(0);
    setJobReloadToken((prev) => prev + 1);
  };

  const handle_change_page = (event, new_page) => {
    setJobListPage(new_page);
  };

  const handle_change_rows_per_page = (event) => {
    const value = Number(event.target.value);
    setJobListPerPage(value);
    setJobListPage(0);
  };

  const handle_job_preview = async (job) => {
    if (!effectiveCustomerId) {
      setImportError("Bitte wählen Sie zuerst einen Kunden aus.");
      return;
    }

    setIsPreviewing(true);
    setImportError(null);
    setJobActionState({ job_id: job.import_job_id, action: "preview" });

    try {
      const response = await previewAssignmentImport(
        effectiveCustomerId,
        job.import_job_id
      );

      const preview_payload = response.preview ?? response;

      const mapped_preview = {
        validation_errors: preview_payload.validation_errors ?? [],
        business_errors: preview_payload.business_errors ?? [],
        diff: preview_payload.diff ?? {
          user_vertrag: { create: [], update_scope: [], delete: [] },
          user_objects: { add: [], remove: [] },
        },
        stats: preview_payload.stats ?? {},
        can_apply: preview_payload.can_apply ?? false,
        warnings: preview_payload.warnings ?? [],
        snapshot_info: preview_payload.snapshot_info ?? null,
        current_info: preview_payload.current_info ?? null,
        rollback_job: null,
      };

      setImportJobId(response.import_job_id ?? job.import_job_id);
      setPreviewResult(mapped_preview);
      setApplyMessage(null);
      setJobStatus(null);
      setCurrentFlow({ type: "import", job, preview: mapped_preview });
      setImportSectionActive(true);
      setJobReloadToken((prev) => prev + 1);
    } catch (err) {
      const error_message =
        err?.response?.data?.error ||
        "Fehler bei der Vorschau. Bitte versuchen Sie es erneut.";
      setImportError(error_message);
    } finally {
      setIsPreviewing(false);
      setJobActionState(null);
    }
  };

  const handle_job_apply = async (job) => {
    if (!effectiveCustomerId) {
      setImportError("Bitte wählen Sie zuerst einen Kunden aus.");
      return;
    }

    setIsApplying(true);
    setImportError(null);
    setApplyMessage(null);
    setJobActionState({ job_id: job.import_job_id, action: "apply" });

    try {
      const response = await applyAssignmentImport(
        effectiveCustomerId,
        job.import_job_id
      );

      setApplyMessage(response.message || "Import wird verarbeitet...");
      await startPollingJobStatus(effectiveCustomerId, job.import_job_id);
      setJobReloadToken((prev) => prev + 1);
    } catch (err) {
      const error_message =
        err?.response?.data?.error ||
        "Fehler beim Anwenden des Imports. Bitte versuchen Sie es erneut.";
      setImportError(error_message);
    } finally {
      setIsApplying(false);
      setJobActionState(null);
    }
  };

  const handle_rollback_preview = useCallback(
    async (job) => {
      if (job_action_state && job_action_state.job_id === job.import_job_id) {
        return; // Already running
      }

      setJobActionState({
        job_id: job.import_job_id,
        action: "rollback_preview",
      });

      try {
        const response = await previewAssignmentRollback(
          effectiveCustomerId,
          job.import_job_id
        );

        const preview_payload = response.preview ?? response;
        const rollback_job_id =
          response.rollback_job_id ?? preview_payload?.rollback_job?.id ?? null;

        const mapped_preview = {
          validation_errors: preview_payload.validation_errors ?? [],
          business_errors: preview_payload.business_errors ?? [],
          diff: preview_payload.diff ?? {
            user_vertrag: { create: [], update_scope: [], delete: [] },
            user_objects: { add: [], remove: [] },
          },
          stats: preview_payload.stats ?? {},
          can_apply: preview_payload.can_apply ?? false,
          warnings: preview_payload.warnings ?? [],
          snapshot_info: preview_payload.snapshot_info ?? null,
          current_info: preview_payload.current_info ?? null,
          rollback_job: preview_payload.rollback_job ?? null,
        };

        setPreviewResult(mapped_preview);
        setSelectedFile(null);
        setImportJobId(null);
        setApplyMessage(null);
        setJobStatus(null);
        setImportError(null);
        setCurrentFlow({
          type: "rollback",
          job,
          preview: mapped_preview,
          rollback_job_id,
        });
        setImportSectionActive(true);
        setJobReloadToken((prev) => prev + 1);
      } catch (error) {
        console.error("Rollback preview failed:", error);
        setJobListError("Fehler beim Laden der Zurücksetzen-Vorschau.");
      } finally {
        setJobActionState(null);
      }
    },
    [effectiveCustomerId, job_action_state]
  );

  const handle_rollback_apply = async (job) => {
    if (!effectiveCustomerId) {
      setImportError("Bitte wählen Sie zuerst einen Kunden aus.");
      return;
    }

    const job_actions = job?.actions?.rollback;
    const rollback_job_id = job_actions?.rollback_job_id;

    if (!rollback_job_id) {
      setImportError(
        "Für diesen Import ist noch kein Rollback-Auftrag angelegt. Bitte zuerst eine Zurücksetzen-Vorschau ausführen."
      );
      return;
    }

    setJobActionState({ job_id: job.import_job_id, action: "rollback_apply" });
    setImportError(null);

    try {
      const response = await applyAssignmentRollback(
        effectiveCustomerId,
        rollback_job_id,
        true
      );

      setApplyMessage(response.message || "Zurücksetzen gestartet");
      setCurrentFlow(null);
      setPreviewResult(null);
      setImportSectionActive(false);
      setJobReloadToken((prev) => prev + 1);
    } catch (err) {
      const error_message =
        err?.response?.data?.error ||
        "Rollback konnte nicht gestartet werden. Bitte versuchen Sie es erneut.";
      setImportError(error_message);
    } finally {
      setJobActionState(null);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!effectiveCustomerId) {
      setTemplateError("Bitte wählen Sie einen Kunden aus.");
      return;
    }

    setIsDownloading(true);
    setTemplateError(null);

    try {
      await downloadAssignmentTemplate(effectiveCustomerId, templateOptions);
    } catch (err) {
      console.error("Template download failed:", err);
      setTemplateError(
        "Fehler beim Herunterladen der Vorlage. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
    // Reset import flow state when new file is selected
    setImportJobId(null);
    setPreviewResult(null);
    setApplyMessage(null);
    setJobStatus(null);
    setImportError(null);
  };

  const handleUploadImport = async () => {
    if (!effectiveCustomerId) {
      setImportError("Bitte wählen Sie einen Kunden aus.");
      return;
    }
    if (!selectedFile) {
      setImportError("Bitte wählen Sie eine Datei aus.");
      return;
    }

    setIsUploading(true);
    setImportError(null);
    setImportJobId(null);
    setPreviewResult(null);
    setApplyMessage(null);
    setJobStatus(null);
    try {
      const res = await uploadAssignmentFile(effectiveCustomerId, selectedFile);
      setImportJobId(res.import_job_id);
      setJobReloadToken((prev) => prev + 1);
    } catch (err) {
      console.error("Upload failed:", err);
      setImportError(
        "Fehler beim Hochladen der Datei. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handlePreviewImport = async () => {
    if (!importJobId) {
      setImportError(
        "Kein Upload gefunden. Bitte laden Sie zuerst eine Datei hoch."
      );
      return;
    }
    setIsPreviewing(true);
    setImportError(null);
    setPreviewResult(null);
    try {
      const res = await previewAssignmentImport(
        effectiveCustomerId,
        importJobId
      );
      setPreviewResult(res);
      setJobReloadToken((prev) => prev + 1);
    } catch (err) {
      console.error("Preview failed:", err);
      setImportError("Fehler bei der Vorschau. Bitte versuchen Sie es erneut.");
    } finally {
      setIsPreviewing(false);
    }
  };

  const handleApplyImport = async () => {
    if (!importJobId) {
      setImportError(
        "Kein Upload gefunden. Bitte laden Sie zuerst eine Datei hoch."
      );
      return;
    }
    if (!previewResult?.can_apply) {
      setImportError(
        "Import kann nicht angewendet werden. Bitte beheben Sie die Fehler."
      );
      return;
    }
    setIsApplying(true);
    setImportError(null);
    setApplyMessage(null);
    try {
      const res = await applyAssignmentImport(effectiveCustomerId, importJobId);
      setApplyMessage(res.message || "Import wird verarbeitet...");

      // Start polling job status until completed/failed
      await startPollingJobStatus(effectiveCustomerId, importJobId);
      setJobReloadToken((prev) => prev + 1);
    } catch (err) {
      console.error("Apply failed:", err);
      setImportError(
        "Fehler beim Anwenden des Imports. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsApplying(false);
    }
  };

  const startPollingJobStatus = async (kundeIdOv, jobId) => {
    setIsPolling(true);
    setJobStatus(null);
    const maxAttempts = 30; // ~30s with 1s interval (plus optional debug delay)
    const intervalMs = 1000;
    let attempt = 0;

    while (attempt < maxAttempts) {
      try {
        const status = await getAssignmentImportJobStatus(kundeIdOv, jobId, {
          debug_delay_ms:
            debugDelayMs && Number.isFinite(Number(debugDelayMs))
              ? Number(debugDelayMs)
              : 0,
        });
        setJobStatus(status);

        if (status.status === "completed" || status.status === "failed") {
          setIsPolling(false);
          setApplyMessage(
            status.status === "completed"
              ? "Import erfolgreich abgeschlossen."
              : "Import fehlgeschlagen."
          );
          return;
        }
      } catch (e) {
        console.error("Status polling failed:", e);
        // Keep polling unless max attempts exceeded
      }

      // wait for next interval
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      attempt++;
    }

    setIsPolling(false);
    setImportError("Zeitüberschreitung beim Abfragen des Import-Status.");
  };

  const handleDownloadCatalogs = async () => {
    if (!effectiveCustomerId) {
      setCatalogsError("Bitte wählen Sie einen Kunden aus.");
      return;
    }

    setIsDownloadingCatalogs(true);
    setCatalogsError(null);

    try {
      await downloadAssignmentCatalogs(effectiveCustomerId, catalogOptions);
    } catch (err) {
      console.error("Catalogs download failed:", err);
      setCatalogsError(
        "Fehler beim Herunterladen der Kataloge. Bitte versuchen Sie es erneut."
      );
    } finally {
      setIsDownloadingCatalogs(false);
    }
  };

  // Helper functions to determine current import step
  const getImportStep = () => {
    if (jobStatus?.status === "completed" || jobStatus?.status === "failed") {
      return "completed";
    }
    if (previewResult) {
      return "preview";
    }
    if (importJobId) {
      return "uploaded";
    }
    return "initial";
  };

  const handleCancelImport = () => {
    setSelectedFile(null);
    setImportJobId(null);
    setPreviewResult(null);
    setApplyMessage(null);
    setJobStatus(null);
    setImportError(null);
    setImportSectionActive(false);
    setCurrentFlow(null);
  };

  const handleStartImport = () => {
    setCurrentFlow({ type: "import", job: null });
    setImportSectionActive(true);
    setPreviewResult(null);
    setImportJobId(null);
    setSelectedFile(null);
    setApplyMessage(null);
    setJobStatus(null);
    setImportError(null);
  };

  const handleOpenTemplateModal = () => {
    setTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = () => {
    setTemplateModalOpen(false);
  };

  const handleFinishCompleted = () => {
    setApplyMessage(null);
    setImportSectionActive(false);
    setPreviewResult(null);
    setImportJobId(null);
    setJobStatus(null);
    setCurrentFlow(null);
  };

  const is_rollback_flow = currentFlow?.type === "rollback";
  const is_applying_rollback =
    is_rollback_flow && job_action_state?.action === "rollback_apply";
  const import_step = getImportStep();
  const show_upload_card =
    import_section_active &&
    !is_rollback_flow &&
    (import_step === "initial" || import_step === "uploaded");
  const upload_step = show_upload_card ? import_step : null;

  const section_title = is_rollback_flow
    ? "Zuordnung zurücksetzen"
    : "Import-Vorschau";
  const preview_intro = is_rollback_flow
    ? "Die Vorschau zeigt alle Änderungen, die durch das Zurücksetzen rückgängig gemacht werden. Prüfen Sie die Details und starten Sie das Zurücksetzen, um den ursprünglichen Zustand wiederherzustellen."
    : "Prüfen Sie die vorgeschlagenen Änderungen und wenden Sie sie bei Bedarf an.";
  const apply_button_label = is_rollback_flow
    ? "Zurücksetzen anwenden"
    : "Import anwenden";
  const cancel_button_label = is_rollback_flow
    ? "Zurücksetzen abbrechen"
    : "Import abbrechen";
  const handle_apply_action =
    is_rollback_flow && currentFlow?.job
      ? () => handle_rollback_apply(currentFlow.job)
      : handleApplyImport;
  const is_applying_action = is_rollback_flow
    ? is_applying_rollback
    : isApplying;

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Verwalten Sie die Zuordnung von Benutzern zu Verträgen und Objekten über
        Excel-Import.
      </Typography>

      {isSysAdmin && (
        <CustomerSelector
          customers={customers}
          selected_customer={selectedCustomer}
          on_customer_change={(event, new_value) => {
            setSelectedCustomer(new_value);
            setTemplateError(null);
            setCatalogsError(null);
            setImportError(null);
          }}
          on_customer_input_change={(event, new_input_value) => {
            if (new_input_value.length >= 2) {
              searchCustomers(new_input_value);
            } else {
              clearSearch();
            }
          }}
          search_loading={searchLoading}
        />
      )}

      {!import_section_active && (
        <WorkflowChooserCard
          on_start_import={handleStartImport}
          on_open_template_modal={handleOpenTemplateModal}
          is_sys_admin={isSysAdmin}
          selected_customer={selectedCustomer}
        />
      )}

      <ImportUploadCard
        is_active={show_upload_card}
        import_error={importError}
        upload_step={upload_step}
        selected_file={selectedFile}
        handle_file_change={handleFileChange}
        handle_upload_import={handleUploadImport}
        is_uploading={isUploading}
        is_sys_admin={isSysAdmin}
        selected_customer={selectedCustomer}
        handle_preview_import={handlePreviewImport}
        is_previewing={isPreviewing}
        handle_cancel={handleCancelImport}
      />

      <ImportPreviewCard
        title={section_title}
        intro={preview_intro}
        import_error={is_rollback_flow ? importError : null}
        preview_result={previewResult}
        handle_apply={handle_apply_action}
        is_applying={is_applying_action}
        handle_cancel={handleCancelImport}
        apply_button_label={apply_button_label}
        cancel_button_label={cancel_button_label}
        debug_delay_ms={debugDelayMs}
        set_debug_delay_ms={setDebugDelayMs}
        apply_message={applyMessage}
        is_polling={isPolling}
        import_step={import_step}
        on_finish_completed={handleFinishCompleted}
        kunde_id_ov={effectiveCustomerId}
        import_job_id={importJobId}
      />

      {!import_section_active && !currentFlow && (
        <JobHistoryCard
          effective_customer_id={effectiveCustomerId}
          job_list={job_list}
          job_list_loading={job_list_loading}
          job_list_error={job_list_error}
          job_status_filter={job_status_filter}
          on_status_filter_change={handle_status_filter_change}
          on_refresh_jobs={handle_refresh_jobs}
          job_list_total={job_list_total}
          job_list_page={job_list_page}
          job_list_per_page={job_list_per_page}
          on_change_page={handle_change_page}
          on_change_rows_per_page={handle_change_rows_per_page}
          on_job_preview={handle_job_preview}
          on_job_apply={handle_job_apply}
          on_rollback_preview={handle_rollback_preview}
          on_rollback_apply={handle_rollback_apply}
          is_job_action_running={is_job_action_running}
        />
      )}

      <TemplateDownloadModal
        open={template_modal_open}
        on_close={handleCloseTemplateModal}
        template_options={templateOptions}
        on_template_options_change={setTemplateOptions}
        on_download_template={handleDownloadTemplate}
        is_downloading_template={isDownloading}
        template_error={templateError}
        catalog_options={catalogOptions}
        on_catalog_options_change={setCatalogOptions}
        on_download_catalogs={handleDownloadCatalogs}
        is_downloading_catalogs={isDownloadingCatalogs}
        catalogs_error={catalogsError}
        is_sys_admin={isSysAdmin}
        selected_customer={selectedCustomer}
      />
    </Box>
  );
};

export default AssignmentImportTabPage;
